


































































































































































































































import { DataTableHeader } from 'vuetify';
import { mapActions, mapGetters } from 'vuex';

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import PriceSnapshotPicker from '@/components/snapshot/PriceSnapshotPicker.vue';
import auth from '@/mixins/auth';
import {
  ICustomerPricingData,
  CustomerPricing,
  CustomerPricingFieldError,
  ICustomerPricing
} from '@/models';
import { Customer } from '@/models/customer';
import { ErrorManager } from '@/models/error';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import CustomerPricingFormDialog from '@/components/customer/CustomerPricingFormDialog.vue';
import CustomerPricingHistoryDialog from '@/components/customer/CustomerPricingHistoryDialog.vue';
import ALPage from '@/components/core/Page/ALPage.vue';
import CustomerAddressUploadItem from '@/components/customer/CustomerAddressUploadItem.vue';
import CustomerUploadItem from '@/components/customer/CustomerUploadItem.vue';

import { ICustomerFilterParams } from '@/services/customer-service.types';
import { IFetchCustomerActionPayload } from '@/store/modules/customer';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';

export default mixins(auth, Functions).extend({
  components: {
    ConfirmationDialog,
    PriceSnapshotPicker,
    ErrorAlert,
    CustomerPricingFormDialog,
    CustomerPricingHistoryDialog,
    ALPage,
    CustomerAddressUploadItem,
    CustomerUploadItem
  },
  data() {
    return {
      loading: true,

      customerPricingDialog: false,
      customerPricingLoading: false,
      customerPricing: null as null | ICustomerPricing,
      fieldError: new CustomerPricingFieldError(),
      customerPricingErrorMessageDetail: '',

      errorMessage: '',
      errorMessageDetail: '',

      filterParams: {
        search: this.$route.query.search as string
      } as ICustomerFilterParams,
      page: Number(this.$route.query.page ?? 1)
    };
  },

  computed: {
    ...mapGetters({
      numCustomers: 'customer/getCustomerCount',
      customers: 'customer/getCustomers',
      getPriceSnapshotById: 'priceSnapshot/getPriceSnapshotById'
    }),

    customerHeaders(): DataTableHeader[] {
      const headers: DataTableHeader[] = [
        {
          text: '',
          value: 'contract',
          sortable: false,
          width: 50
        },
        {
          text: 'ID',
          align: 'start',
          sortable: false,
          value: 'id',
          width: 100
        },
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Address', value: 'address1', sortable: false, width: 300 },

        { text: 'Currency', value: 'currency', sortable: false, width: 100 },
        { text: 'Tax ID', value: 'tax_id', sortable: false, width: 85 },
        { text: 'Term', value: 'terms', sortable: false, width: 85 },
        {
          text: '',
          value: 'actions',
          sortable: false,
          width: 70
        }
      ];

      return headers;
    }
  },

  watch: {
    'filterParams.search'(newSearch) {
      const filterParams: IFetchCustomerActionPayload = {
        page: 1,
        filters: {
          search: newSearch
        }
      };
      this.loading = true;

      this.debounceFetchCustomers(filterParams).then(() => {
        this.page = 1;
      });
    }
  },

  mounted() {
    this.loading = true;
    this.searchCustomers({
      page: this.page,
      filters: this.filterParams
    });
  },

  methods: {
    ...mapActions({
      fetchCustomers: 'customer/fetchCustomers',
      deleteCustomerPricing: 'customer/deleteCustomerPricing'
    }),

    clearAllErrors() {
      this.errorMessage = '';
      this.errorMessageDetail = '';

      this.customerPricingErrorMessageDetail = '';
      this.fieldError.clearErrors();
    },

    openCustomerPricingSnapshotDeleteDialog(contract: CustomerPricing) {
      this.customerPricing = contract;
      this.customerPricingDialog = true;
    },

    closeCustomerPricingSnapshotDeleteDialog() {
      this.customerPricing = null;
      this.customerPricingErrorMessageDetail = '';
      this.customerPricingDialog = false;
    },

    openCustomerPricingHistoryDialog(customer: Customer) {
      const customerPricingHistoryDialog = this.$refs
        .customerPricingHistoryDialog as InstanceType<
        typeof CustomerPricingHistoryDialog
      >;

      customerPricingHistoryDialog.open(customer);
    },

    openCustomerPricingSnapshotDialog(customer: Customer) {
      const customerPricingFormDialog = this.$refs
        .customerPricingFormDialog as InstanceType<
        typeof CustomerPricingFormDialog
      >;
      // in the case where we are creating the pricing would be null and the lookup field (customer) will also be null
      const customerPricing = new CustomerPricing({
        ...customer.contract,
        customer: customer.id!
      });

      customerPricingFormDialog.open(
        customerPricing,
        !!customerPricing.snapshot
      );
    },

    unassignCustomerPricing(data: ICustomerPricingData) {
      this.customerPricingLoading = true;

      this.deleteCustomerPricing(data)
        .then(() => {
          this.customerPricingErrorMessageDetail = '';
          this.customerPricingDialog = false;
        })
        .catch(error => {
          if (error.response) {
            // client received an error response that falls out of range 2xx
            if (
              CustomerPricingFieldError.isCustomerPricingFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new CustomerPricingFieldError(
                error.response.data
              );
            }
          }

          this.customerPricingErrorMessageDetail = ErrorManager.extractApiError(
            error
          );
        })
        .finally(() => {
          this.customerPricingLoading = false;
        });
    },

    onUpdatePage(newPage: number) {
      if (newPage !== this.page) {
        this.debounceFetchCustomers({
          page: newPage,
          filters: this.filterParams
        }).then(() => {
          this.page = newPage;
        });
      }
    },

    async debounceFetchCustomers(params: IFetchCustomerActionPayload) {
      this.clearAllErrors();
      this.loading = true;

      return await this.debounce(this.searchCustomers, 200)(params);
    },

    searchCustomers(payload: IFetchCustomerActionPayload) {
      /* this will update the URL query based on param values. */
      this.$router
        .replace({
          name: 'customer-list',
          query: { page: payload.page, ...payload.filters } as Record<
            string,
            any
          >
        })
        .catch(e => e); //catch navigation duplication error

      return this.fetchCustomers(payload)
        .catch(error => {
          if (error.response && error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    reopenCustomerUpload(err?: any) {
      const upload = (this as any).$refs.uploadCustomerfile;
      upload.dialog = true;
    },

    reopenAddressUpload(err?: any) {
      const upload = (this as any).$refs.uploadAddressfile;
      upload.dialog = true;
    },

    goToCustomer(customer: any) {
      this.$router.push({
        name: 'customer-detail',
        params: { customerId: customer.id }
      });
    }
  }
});
